import type { FC } from 'react';
import { ExceptionHandlingWrapper } from './ExceptionHandling.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import exceptions from './../../../resources/content/exceptions.png';

interface ExceptionHandlingProps { }

const ExceptionHandling: FC<ExceptionHandlingProps> = () => (
   <ExceptionHandlingWrapper>
      <header className="entry-header ">
         <h1 className="entry-title" itemProp="headline">Exception Handling</h1>
      </header>
      <div className="entry-content clear" itemProp="text" style={{ height: 'auto !important' }}>
         <p className="text-align-justify">An exception is an event that occurs at run time and disrupts the normal flow of a program’s execution.<br />The Exception Handling is a mechanism to handle the runtime errors caused by exceptions.Handling an exception is making sure that all the statements execute and the flow of the program doesn’t break.</p>
         <br/>
         <h4 className="text-align-justify"><strong>Exception vs Error :</strong></h4>
         <p className="text-align-justify">Exception breaks the flow of the program due to some issues in code while Error breaks the flow of the program due to OS, JVM, Hardware, etc.&nbsp;An exception is recoverable while an error is irrecoverable e.g. OutOfMemoryError, AssertionError, etc</p>
         <br/>
         <h4 className="text-align-left"><strong>Advantage of Exception Handling :</strong></h4>
         <p className="text-align-justify">The advantage of exception handling is that it not only generates exception but also rectify the problem. Exception handling is done where the problem of the source is present.</p>
         <br/>
         <h4 className="text-align-justify"><strong>How it  works?</strong></h4>
         <ul className="text-align-justify">
            <li>If an exception has occurred inside a method, the method creates an Object known as Exception Object and hands it off to JVM.</li>
            <li>The exception object contains the name and description of the exception and the current state of the program where the exception has occurred.</li>
            <li>An exception is a process of creating the Exception Object and handling it to the JVM.</li>
            <li>The JVM searches the call stack(ordered list of methods) to find the method that contains a block of code that can handle the occurred exception. The block of the code is called an Exception handler.</li>
            <li>If it finds an appropriate handler(a type of the exception object thrown matches the type of the exception object it can handle) then it passes the occurred exception to it.</li>
            <li>If it couldn’t find the appropriate handler then JVM handover the Exception Object to the default exception handler.</li>
         </ul>
         <br/>
         <h4 className="text-align-justify"><strong>Types of Exception :</strong></h4>
         <div className="is-style-default">
            <figure className="aligncenter size-large">
               <img decoding="async" width="651" height="479" src={exceptions} alt="" className="lazy-loaded" sizes="(max-width: 651px) 100vw, 651px" />
            </figure>
         </div>
         <br/>
         <ul className="text-align-justify">
            <li><strong>Checked Exception : </strong>
               <ul>
                  <li>Checked exceptions include the classes which directly inherit Throwable class except for RuntimeException and Error.</li>
                  <li> These exceptions are checked at compile-time.</li>
                  <li>Eg – IOException, SQLException etc, RemoteException, ClassNotFoundException, InterruptedException, etc.</li>
               </ul>
            </li>
            <br/>
            <li><strong>Unchecked Exception : </strong>
               <ul>
                  <li>Unchecked exceptions include the classes which inherit RuntimeException.</li>
                  <li>These exceptions are checked at run-time.</li>
                  <li>Eg – ArithmeticException, NullPointerException, ArrayIndexOutOfBoundsException, NumberFormatException.</li>
               </ul>
            </li>
         </ul>
         <br/>
         <h4 className="text-align-justify"><strong>Exception Handling Keywords :</strong></h4>
         <h4 className="text-align-justify">try :</h4>
         <ul className="text-align-justify">
            <li>Try block is where we should place an exception code. </li>
            <li>Either catch or finally, block follows the try block, we can’t use try block alone.</li>
            <li>When JVM gives the try-catch block, the control goes to the end of the try block and after the catch, control goes to OS. When we give our try-catch block, control comes back to us.</li>
         </ul>
         <h4 className="text-align-justify">catch :</h4>
         <ul className="text-align-justify" style={{ height: 'auto !important' }}>
            <li>catch keyword handles the exception. </li>
            <li>We can’t use catch block alone, it must be preceded by a try block.</li>
            <li>It can be followed by finally block later.</li>
            <li>If an exception generated, control goes in catch and then finally block.</li>
            <li>The catch block checks the compatibility. It executes the code and bypassing other catches, it executes the catch of JVM if compatible&nbsp;else only executes the code in the catch of JVM.</li>
         </ul>
         <h4 className="text-align-justify">finally :</h4>
         <ul className="text-align-justify">
            <li>It is used to execute the important code of the program.</li>
            <li>Its execution is final whether the catch handles an exception or not.</li>
            <li>If it doesn’t generate an exception, through finally block control goes to catch of JVM.</li>
            <li>It is useful in file handling for closing connection.</li>
         </ul>
         <h4 className="text-align-justify">throw :</h4>
         <ul className="text-align-justify">
            <li>throw keyword throws an exception manually.</li>
            <li>This keyword is followed by an instance of the Exception class.</li>
            <li>We write it in the method body to throw an exception.</li>
            <li>It can throw one exception at a time.</li>
            <li>The line of generation (throw) should be enclosed in a try block followed by an appropriate catch block(pressure handling).</li>
         </ul>
         <h4 className="text-align-justify">throws :</h4>
         <ul className="text-align-justify">
            <li>throws keyword is used to declare exceptions.</li>
            <li>This works similarly to the try-catch block.</li>
            <li>However, it doesn’t throw an exception.</li>
            <li>In other words, it specifies that there may occur an exception in the method.</li>
            <li>In addition, it is always used with a method signature.</li>
            <li>It is followed by exception class names.</li>
            <li>It can handle multiple exceptions at a time.</li>
            <li>If a method is declared with a throws keyword then it should be called from within a try block followed by a catch block.</li>
         </ul>
         <br/>
         <h4 className="text-align-justify"><strong>To handle exception :</strong></h4>
         <ul className="text-align-justify">
            <li>
               First of all, identify the line which may cause an exception.
               &nbsp;<span className="fa fa-angle-right"></span>&nbsp;
               <strong>z=x/y;</strong>
            </li>
            <li>
               Then, identify the lines which depend on the line which may cause an exception.
               &nbsp;<span className="fa fa-angle-right"></span>&nbsp;
               <strong>System.out.println(z);</strong>
            </li>
            <li>
               After that, identify the type of exception.
               &nbsp;<span className="fa fa-angle-right"></span>&nbsp;
               <strong>Arithmetic Exception</strong>
            </li>
            <li>Finally, put the identified lines in the try block immediately followed by a catch block with the identified exception class pointer.</li>
         </ul>
         <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
            {
               'public class ExceptionDemo {\n' +
               '  //throws keyword\n' +
               '  int division(int a, int b) throws ArithmeticException {\n' +
               '    int result = a / b;\n' +
               '    if (a == 0 && b == 0) {\n' +
               '      //throw keyword\n' +
               '      throw new ArithmeticException("Divide by zero error");\n' +
               '    }\n' +
               '    return result;\n' +
               '  }\n' +
               '  public static void main(String args[]) {\n' +
               '    //try, catch, finally block\n' +
               '    try {\n' +
               '      //code that may cause exception\n' +
               '      int output = 100 / 0;\n' +
               '      OR\n' +
               '      System.out.println(division(5, 10));\n' +
               '    } catch (ArithmeticException e) {\n' +
               '      System.out.println(e);\n' +
               '    } finally {\n' +
               '      System.out.println("Rest of the code....");\n' +
               '    }\n' +
               '  }\n' +
               '}'
            }
         </SyntaxHighlighter>
         <br/>
         <p className="text-align-justify has-text-color" style={{ color: '#ff060e' }}><strong>Note :</strong></p>
         <p className="text-align-justify has-accent-color has-text-color has-normal-font-size"><strong>The&nbsp;finally block&nbsp;will always execute even if an exception occurs or if a return statement is present in a method.&nbsp;After the ‘finally’ block execution is completed, control goes back to the return statement in the try block. If there is a return statement in the ‘finally’ block, then the return statements from try/catch blocks will be overridden.</strong></p>
      </div>
   </ExceptionHandlingWrapper>
);

export default ExceptionHandling;
